import { SalesInvoiceBase } from '../abstract/sales-invoice-base.model';
import { Quotation } from '../quotation/quotation.model';
import { SalesInvoiceAllocation } from './sales-invoice-allocation.model';
import { SalesInvoiceItem } from './sales-invoice-item.model';
import { SalesInvoiceTax } from './sales-invoice-tax.model';
import { InvoiceTypeEnum } from 'src/app/@shared/enums/invoice-type.enum';
import { NestedField, OmitField } from 'src/app/@shared/decorators/api-field.decorator';

export class SalesInvoice extends SalesInvoiceBase {

    static override CLASS_NAME: string = 'SalesInvoice';

    private _allocations!: SalesInvoiceAllocation[];
    private _customerLabelProposal!: string;
    private _downpaymentGrossAmountUsage!: number;
    private _downpaymentGrossAmountUsageOrg!: number;
    private _downpaymentNetAmountUsage!: number;
    private _downpaymentNetAmountUsageOrg!: number;
    private _downpaymentTaxAmountUsage!: number;
    private _downpaymentTaxAmountUsageOrg!: number;
    private _generalConditions!: string;
    private _items!: SalesInvoiceItem[];
    private _purchaseOrderNumber!: string;
    private _quotation?: Quotation;
    private _taxes!: SalesInvoiceTax[];

    constructor(data: any) {
        super(data);
        this._allocations = this._allocations || [];
        this._items = this._items || [];
        this._taxes = this._taxes || [];
    }

    @NestedField()
    public get allocations(): SalesInvoiceAllocation[] {
        return this._allocations;
    }

    public set allocations(value: any) {
        this._setValue('_allocations', value, 'SalesInvoiceAllocation');
    }

    /**
     * Libellé client lu par l'OCR qu'il y a lieu de proposer de retenir sur la fiche
     * du client de la facture. Chaîne vide = rien à proposer : le serveur porte seul
     * les règles (libellé déjà retenu, déjà écarté, ou client trouvé automatiquement).
     * Servi en lecture unitaire uniquement — toujours vide en liste.
     */
    @OmitField()
    public get customerLabelProposal(): string {
        return this._customerLabelProposal;
    }

    public set customerLabelProposal(value: any) {
        this._setValue('_customerLabelProposal', value, 'string');
    }

    @OmitField()
    public get downpaymentGrossAmountUsage(): number {
        return this._downpaymentGrossAmountUsage;
    }

    public set downpaymentGrossAmountUsage(value: number) {
        this._setValue('_downpaymentGrossAmountUsage', value, 'float');
    }

    @OmitField()
    public get downpaymentGrossAmountUsageOrg(): number {
        return this._downpaymentGrossAmountUsageOrg;
    }

    public set downpaymentGrossAmountUsageOrg(value: number) {
        this._setValue('_downpaymentGrossAmountUsageOrg', value, 'float');
    }

    @OmitField()
    public get downpaymentNetAmountUsage(): number {
        return this._downpaymentNetAmountUsage;
    }

    public set downpaymentNetAmountUsage(value: number) {
        this._setValue('_downpaymentNetAmountUsage', value, 'float');
    }

    @OmitField()
    public get downpaymentNetAmountUsageOrg(): number {
        return this._downpaymentNetAmountUsageOrg;
    }

    public set downpaymentNetAmountUsageOrg(value: number) {
        this._setValue('_downpaymentNetAmountUsageOrg', value, 'float');
    }

    @OmitField()
    public get downpaymentTaxAmountUsage(): number {
        return this._downpaymentTaxAmountUsage;
    }

    public set downpaymentTaxAmountUsage(value: number) {
        this._setValue('_downpaymentTaxAmountUsage', value, 'float');
    }

    @OmitField()
    public get downpaymentTaxAmountUsageOrg(): number {
        return this._downpaymentTaxAmountUsageOrg;
    }

    public set downpaymentTaxAmountUsageOrg(value: number) {
        this._setValue('_downpaymentTaxAmountUsageOrg', value, 'float');
    }

    public get generalConditions(): string {
        return this._generalConditions;
    }

    public set generalConditions(value: string) {
        this._setValue('_generalConditions', value, 'string');
    }

    public get isFinalInvoice(): boolean {
        return this.type.code === InvoiceTypeEnum.Balance || this.type.code === InvoiceTypeEnum.Classic;
    }

    /**
     * La facture peut servir de modèle à une facture récurrente : elle doit avoir été
     * saisie dans Biifor (les origines importées n'ont ni lignes ni catalogue) et être
     * une facture classique (avoir, acompte, situation et solde sont adossés à un
     * contexte non reproductible).
     */
    public get isRecurrable(): boolean {
        return this.isInternal && this.type.code === InvoiceTypeEnum.Classic;
    }

    @NestedField()
    public get items(): SalesInvoiceItem[] {
        return this._items;
    }

    public set items(value: any) {
        this._setValue('_items', value, 'SalesInvoiceItem');
    }

    public get purchaseOrderNumber(): string {
        return this._purchaseOrderNumber;
    }

    public set purchaseOrderNumber(value: string) {
        this._setValue('_purchaseOrderNumber', value, 'string');
    }

    @OmitField()
    public get quotation(): Quotation | undefined {
        return this._quotation;
    }

    public set quotation(value: any) {
        this._setValue('_quotation', value, 'Quotation');
    }

    @NestedField()
    public get taxes(): SalesInvoiceTax[] {
        return this._taxes;
    }

    public set taxes(value: any) {
        this._setValue('_taxes', value, 'SalesInvoiceTax');
    }
}
